import { TFunction } from "i18next";
import { PhonemeTooltipMap } from "shared/models";
import { Trans } from "react-i18next";

interface PhonemeExample {
  mainText: string;
  tooltipTexts: { text: string; index: number }[];
  language: string;
}

export const getPhonemeTooltipMapping = (t: TFunction): PhonemeTooltipMap => {
  /**
   * mainText: main text string.
   * tooltipTexts: array of objects in the text to underline and its index in the mainText (index and text should match with main Text.
   * language: language code.
   */
  const renderPhonemeExample = (
    examples: PhonemeExample[],
    character: string
  ) => (
    <div>
      {examples.map((example, index) => (
        <div key={index}>
          <span id="ph-main">
            <span style={{ fontWeight: "bold" }}>
              {example.mainText.split("").map((char, charIndex) => {
                const tooltip = example.tooltipTexts.find(
                  (t) => t.index === charIndex
                );
                return tooltip ? (
                  <span
                    key={charIndex}
                    id="ph-tooltip"
                    style={{ textDecoration: "underline" }}
                  >
                    {tooltip.text}
                  </span>
                ) : (
                  char
                );
              })}{" "}
            </span>
            ({example.language})
          </span>
          <br />
        </div>
      ))}
      {character && (
        <Trans
          i18nKey={`tts.toolbarMenu.pronunciationMenu.examples.${character}`}
          components={{ i: <i /> }}
        />
      )}
    </div>
  );

  return {
    æ: renderPhonemeExample(
      [
        {
          mainText: "land",
          tooltipTexts: [{ text: "a", index: 1 }],
          language: "en",
        },
      ],
      ""
    ),
    ɪ: renderPhonemeExample(
      [
        {
          mainText: "sit",
          tooltipTexts: [{ text: "i", index: 1 }],
          language: "en",
        },
        {
          mainText: "Sinn",
          tooltipTexts: [{ text: "i", index: 1 }],
          language: "de",
        },
      ],
      ""
    ),
    i: renderPhonemeExample(
      [
        {
          mainText: "eat",
          tooltipTexts: [
            { text: "e", index: 0 },
            { text: "a", index: 1 },
          ],
          language: "en",
        },
        {
          mainText: "Sieb",
          tooltipTexts: [
            { text: "i", index: 1 },
            { text: "e", index: 2 },
          ],
          language: "de",
        },
      ],
      ""
    ),
    ɑ: renderPhonemeExample(
      [
        {
          mainText: "calm",
          tooltipTexts: [{ text: "a", index: 1 }],
          language: "en",
        },
      ],
      ""
    ),
    a: renderPhonemeExample(
      [
        {
          mainText: "Mann",
          tooltipTexts: [{ text: "a", index: 1 }],
          language: "de",
        },
        {
          mainText: "avoir",
          tooltipTexts: [{ text: "a", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    ɜ: renderPhonemeExample(
      [
        {
          mainText: "bird",
          tooltipTexts: [
            { text: "i", index: 1 },
            { text: "r", index: 2 },
          ],
          language: "en-GB",
        },
      ],
      "ɜ"
    ),
    œ: renderPhonemeExample(
      [
        {
          mainText: "können",
          tooltipTexts: [{ text: "ö", index: 1 }],
          language: "de",
        },
        {
          mainText: "boeuf",
          tooltipTexts: [
            { text: "o", index: 1 },
            { text: "e", index: 2 },
            { text: "u", index: 3 },
          ],
          language: "fr",
        },
      ],
      ""
    ),

    ɔ: renderPhonemeExample(
      [
        {
          mainText: "offen",
          tooltipTexts: [{ text: "o", index: 0 }],
          language: "de",
        },
        {
          mainText: "pote",
          tooltipTexts: [{ text: "o", index: 1 }],
          language: "fr",
        },
      ],
      ""
    ),

    ʌ: renderPhonemeExample(
      [
        {
          mainText: "enough",
          tooltipTexts: [
            { text: "o", index: 2 },
            { text: "u", index: 3 },
          ],
          language: "en",
        },
      ],
      ""
    ),
    ɛ: renderPhonemeExample(
      [
        {
          mainText: "bed",
          tooltipTexts: [{ text: "e", index: 1 }],
          language: "en",
        },
        {
          mainText: "essen",
          tooltipTexts: [{ text: "e", index: 0 }],
          language: "de",
        },
        {
          mainText: "frère",
          tooltipTexts: [{ text: "è", index: 2 }],
          language: "fr",
        },
      ],
      ""
    ),

    e: renderPhonemeExample(
      [
        {
          mainText: "Beet",
          tooltipTexts: [
            { text: "e", index: 1 },
            { text: "e", index: 2 },
          ],
          language: "de",
        },
        {
          mainText: "école",
          tooltipTexts: [{ text: "é", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    ʏ: renderPhonemeExample(
      [
        {
          mainText: "müssen",
          tooltipTexts: [{ text: "ü", index: 1 }],
          language: "de",
        },
      ],
      ""
    ),
    ɒ: renderPhonemeExample(
      [
        {
          mainText: "pot",
          tooltipTexts: [{ text: "o", index: 1 }],
          language: "en-GB",
        },
      ],
      ""
    ),

    ʊ: renderPhonemeExample(
      [
        {
          mainText: "should",
          tooltipTexts: [
            { text: "o", index: 2 },
            { text: "u", index: 3 },
          ],
          language: "en",
        },
        {
          mainText: "und",
          tooltipTexts: [{ text: "u", index: 0 }],
          language: "de",
        },
      ],
      ""
    ),
    y: renderPhonemeExample(
      [
        {
          mainText: "üben",
          tooltipTexts: [{ text: "ü", index: 0 }],
          language: "de",
        },
        {
          mainText: "jupe",
          tooltipTexts: [{ text: "u", index: 1 }],
          language: "fr",
        },
      ],
      ""
    ),
    ɨ: renderPhonemeExample(
      [
        {
          mainText: "syn",
          tooltipTexts: [{ text: "y", index: 1 }],
          language: "pl",
        },
        {
          mainText: "быть",
          tooltipTexts: [{ text: "ы", index: 1 }],
          language: "ru",
        },
      ],
      ""
    ),

    u: renderPhonemeExample(
      [
        {
          mainText: "use",
          tooltipTexts: [{ text: "u", index: 0 }],
          language: "en",
        },
        {
          mainText: "suchen",
          tooltipTexts: [{ text: "u", index: 1 }],
          language: "de",
        },
        {
          mainText: "loup",
          tooltipTexts: [
            { text: "o", index: 1 },
            { text: "u", index: 2 },
          ],
          language: "fr",
        },
      ],
      ""
    ),

    o: renderPhonemeExample(
      [
        {
          mainText: "oder",
          tooltipTexts: [{ text: "o", index: 0 }],
          language: "de",
        },
      ],
      ""
    ),
    ø: renderPhonemeExample(
      [
        {
          mainText: "lösen",
          tooltipTexts: [{ text: "ö", index: 1 }],
          language: "de",
        },
        {
          mainText: "deux",
          tooltipTexts: [
            { text: "e", index: 1 },
            { text: "u", index: 2 },
          ],
          language: "fr",
        },
      ],
      ""
    ),

    õ: renderPhonemeExample(
      [
        {
          mainText: "onze",
          tooltipTexts: [{ text: "o", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    ã: renderPhonemeExample(
      [
        {
          mainText: "ans",
          tooltipTexts: [
            { text: "a", index: 0 },
            { text: "n", index: 1 },
          ],
          language: "de",
        },
      ],
      ""
    ),
    ɐ: renderPhonemeExample(
      [
        {
          mainText: "Wasser",
          tooltipTexts: [
            { text: "e", index: 4 },
            { text: "r", index: 5 },
          ],
          language: "de",
        },
      ],
      ""
    ),
    ɝ: renderPhonemeExample(
      [
        {
          mainText: "bird",
          tooltipTexts: [
            { text: "i", index: 1 },
            { text: "r", index: 2 },
          ],
          language: "en-US",
        },
      ],
      "ɝ"
    ),
    ə: renderPhonemeExample(
      [
        {
          mainText: "London",
          tooltipTexts: [{ text: "o", index: 4 }],
          language: "en",
        },
        {
          mainText: "Besuch",
          tooltipTexts: [{ text: "e", index: 1 }],
          language: "de",
        },
        {
          mainText: "le",
          tooltipTexts: [{ text: "e", index: 1 }],
          language: "fr",
        },
      ],
      ""
    ),

    ɚ: renderPhonemeExample(
      [
        {
          mainText: "bird",
          tooltipTexts: [{ text: "i", index: 1 }],
          language: "en-US",
        },
      ],
      ""
    ),

    z: renderPhonemeExample(
      [
        {
          mainText: "zoo",
          tooltipTexts: [{ text: "z", index: 0 }],
          language: "en",
        },
        {
          mainText: "Sinn",
          tooltipTexts: [{ text: "S", index: 0 }],
          language: "de",
        },
        {
          mainText: "zèbre",
          tooltipTexts: [{ text: "z", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    r: renderPhonemeExample(
      [
        {
          mainText: "perro",
          tooltipTexts: [
            { text: "r", index: 2 },
            { text: "r", index: 3 },
          ],
          language: "es",
        },
      ],
      "r"
    ),
    g: renderPhonemeExample(
      [
        {
          mainText: "ago",
          tooltipTexts: [{ text: "g", index: 1 }],
          language: "en",
        },
        {
          mainText: "gleich",
          tooltipTexts: [{ text: "g", index: 0 }],
          language: "de",
        },
        {
          mainText: "gâteau",
          tooltipTexts: [{ text: "g", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    d: renderPhonemeExample(
      [
        {
          mainText: "dense",
          tooltipTexts: [{ text: "d", index: 0 }],
          language: "en",
        },
        {
          mainText: "Delfin",
          tooltipTexts: [{ text: "D", index: 0 }],
          language: "de",
        },
        {
          mainText: "dauphin",
          tooltipTexts: [{ text: "d", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    n: renderPhonemeExample(
      [
        {
          mainText: "know",
          tooltipTexts: [
            { text: "k", index: 0 },
            { text: "n", index: 1 },
          ],
          language: "en",
        },
        {
          mainText: "nennen",
          tooltipTexts: [
            { text: "n", index: 0 },
            { text: "n", index: 2 },
            { text: "n", index: 3 },
            { text: "n", index: 5 },
          ],
          language: "de",
        },
        {
          mainText: "nuance",
          tooltipTexts: [{ text: "n", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    t: renderPhonemeExample(
      [
        {
          mainText: "tennis",
          tooltipTexts: [{ text: "t", index: 0 }],
          language: "en",
        },
      ],
      ""
    ),
    ʒ: renderPhonemeExample(
      [
        {
          mainText: "vision",
          tooltipTexts: [
            { text: "s", index: 2 },
            { text: "i", index: 3 },
          ],
          language: "en",
        },
        {
          mainText: "Ingenieur",
          tooltipTexts: [
            { text: "g", index: 2 },
            { text: "e", index: 3 },
          ],
          language: "de",
        },
        {
          mainText: "jupe",
          tooltipTexts: [{ text: "j", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    l: renderPhonemeExample(
      [
        {
          mainText: "London",
          tooltipTexts: [{ text: "L", index: 0 }],
          language: "en",
        },
        {
          mainText: "egal",
          tooltipTexts: [{ text: "l", index: 3 }],
          language: "de",
        },
        {
          mainText: "lapin",
          tooltipTexts: [{ text: "l", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    v: renderPhonemeExample(
      [
        {
          mainText: "above",
          tooltipTexts: [
            { text: "v", index: 3 },
            { text: "e", index: 4 },
          ],
          language: "en",
        },
        {
          mainText: "Wasser",
          tooltipTexts: [{ text: "W", index: 0 }],
          language: "de",
        },
        {
          mainText: "voler",
          tooltipTexts: [{ text: "v", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    w: renderPhonemeExample(
      [
        {
          mainText: "water",
          tooltipTexts: [{ text: "w", index: 0 }],
          language: "en",
        },
        {
          mainText: "oui",
          tooltipTexts: [
            { text: "o", index: 0 },
            { text: "u", index: 1 },
          ],
          language: "fr",
        },
      ],
      ""
    ),
    b: renderPhonemeExample(
      [
        {
          mainText: "burn",
          tooltipTexts: [{ text: "b", index: 0 }],
          language: "en",
        },
        {
          mainText: "aber",
          tooltipTexts: [{ text: "b", index: 1 }],
          language: "de",
        },
        {
          mainText: "arabe",
          tooltipTexts: [{ text: "b", index: 3 }],
          language: "fr",
        },
      ],
      ""
    ),

    s: renderPhonemeExample(
      [
        {
          mainText: "sense",
          tooltipTexts: [
            { text: "s", index: 0 },
            { text: "s", index: 3 },
            { text: "e", index: 4 },
          ],
          language: "en",
        },
        {
          mainText: "essen",
          tooltipTexts: [
            { text: "s", index: 1 },
            { text: "s", index: 2 },
          ],
          language: "de",
        },
        {
          mainText: "séance",
          tooltipTexts: [
            { text: "s", index: 0 },
            { text: "c", index: 4 },
            { text: "e", index: 5 },
          ],
          language: "fr",
        },
      ],
      ""
    ),

    k: renderPhonemeExample(
      [
        {
          mainText: "chemistry",
          tooltipTexts: [
            { text: "c", index: 0 },
            { text: "h", index: 1 },
          ],
          language: "en",
        },
        {
          mainText: "Kayak",
          tooltipTexts: [{ text: "K", index: 0 }],
          language: "de",
        },
        {
          mainText: "canal",
          tooltipTexts: [{ text: "c", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),

    f: renderPhonemeExample(
      [
        {
          mainText: "beef",
          tooltipTexts: [{ text: "f", index: 3 }],
          language: "en",
        },
        {
          mainText: "fahren",
          tooltipTexts: [{ text: "f", index: 0 }],
          language: "de",
        },
        {
          mainText: "frère",
          tooltipTexts: [{ text: "f", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    ɹ: renderPhonemeExample(
      [
        {
          mainText: "road",
          tooltipTexts: [{ text: "r", index: 0 }],
          language: "en",
        },
      ],
      "ɹ"
    ),
    m: renderPhonemeExample(
      [
        {
          mainText: "calm",
          tooltipTexts: [{ text: "m", index: 3 }],
          language: "en",
        },
        {
          mainText: "Mutter",
          tooltipTexts: [{ text: "M", index: 0 }],
          language: "de",
        },
        {
          mainText: "âme",
          tooltipTexts: [{ text: "m", index: 1 }],
          language: "fr",
        },
      ],
      ""
    ),

    ð: renderPhonemeExample(
      [
        {
          mainText: "this",
          tooltipTexts: [
            { text: "t", index: 0 },
            { text: "h", index: 1 },
          ],
          language: "en",
        },
      ],
      ""
    ),

    j: renderPhonemeExample(
      [
        {
          mainText: "lawyer",
          tooltipTexts: [{ text: "y", index: 3 }],
          language: "en",
        },
        {
          mainText: "Junge",
          tooltipTexts: [{ text: "J", index: 0 }],
          language: "de",
        },
        {
          mainText: "feuille",
          tooltipTexts: [
            { text: "l", index: 4 },
            { text: "l", index: 5 },
            { text: "e", index: 6 },
          ],
          language: "fr",
        },
      ],
      ""
    ),
    ʃ: renderPhonemeExample(
      [
        {
          mainText: "delicious",
          tooltipTexts: [
            { text: "c", index: 4 },
            { text: "i", index: 5 },
          ],
          language: "en",
        },
        {
          mainText: "Schnee",
          tooltipTexts: [
            { text: "S", index: 0 },
            { text: "c", index: 1 },
            { text: "h", index: 2 },
          ],
          language: "de",
        },
        {
          mainText: "chercher",
          tooltipTexts: [
            { text: "c", index: 0 },
            { text: "h", index: 1 },
            { text: "c", index: 4 },
            { text: "h", index: 5 },
          ],
          language: "fr",
        },
      ],
      ""
    ),

    h: renderPhonemeExample(
      [
        {
          mainText: "hamster",
          tooltipTexts: [{ text: "h", index: 0 }],
          language: "en",
        },
        {
          mainText: "Hund",
          tooltipTexts: [{ text: "H", index: 0 }],
          language: "de",
        },
      ],
      ""
    ),

    ɳ: renderPhonemeExample(
      [
        {
          mainText: "cognac",
          tooltipTexts: [
            { text: "g", index: 2 },
            { text: "n", index: 3 },
          ],
          language: "en",
        },
      ],
      ""
    ),

    p: renderPhonemeExample(
      [
        {
          mainText: "soap",
          tooltipTexts: [{ text: "p", index: 3 }],
          language: "en",
        },
        {
          mainText: "Pappe",
          tooltipTexts: [
            { text: "P", index: 0 },
            { text: "p", index: 2 },
            { text: "p", index: 3 },
          ],
          language: "de",
        },
        {
          mainText: "pouvoir",
          tooltipTexts: [{ text: "p", index: 0 }],
          language: "fr",
        },
      ],
      ""
    ),
    ŋ: renderPhonemeExample(
      [
        {
          mainText: "camping",
          tooltipTexts: [
            { text: "n", index: 5 },
            { text: "g", index: 6 },
          ],
          language: "en",
        },
        {
          mainText: "singen",
          tooltipTexts: [
            { text: "n", index: 2 },
            { text: "g", index: 3 },
          ],
          language: "de",
        },
      ],
      ""
    ),

    θ: renderPhonemeExample(
      [
        {
          mainText: "both",
          tooltipTexts: [
            { text: "t", index: 2 },
            { text: "h", index: 3 },
          ],
          language: "en",
        },
      ],
      ""
    ),

    ʔ: renderPhonemeExample(
      [
        {
          mainText: "uh[_]oh",
          tooltipTexts: [],
          language: "en",
        },
        {
          mainText: "be[_]achten",
          tooltipTexts: [],
          language: "de",
        },
      ],
      ""
    ),

    x: renderPhonemeExample(
      [
        {
          mainText: "Bach",
          tooltipTexts: [
            { text: "c", index: 2 },
            { text: "h", index: 3 },
          ],
          language: "de",
        },
      ],
      ""
    ),
    ʁ: renderPhonemeExample(
      [
        {
          mainText: "Karren",
          tooltipTexts: [
            { text: "r", index: 2 },
            { text: "r", index: 3 },
          ],
          language: "de",
        },
        {
          mainText: "rouge",
          tooltipTexts: [{ text: "r", index: 0 }],
          language: "fr",
        },
      ],
      "ʁ"
    ),
    R: renderPhonemeExample(
      [
        {
          mainText: "Riesenrad",
          tooltipTexts: [
            { text: "R", index: 0 },
            { text: "r", index: 6 },
          ],
          language: "de",
        },
      ],
      "R"
    ),
    ç: renderPhonemeExample(
      [
        {
          mainText: "ich",
          tooltipTexts: [
            { text: "c", index: 1 },
            { text: "h", index: 2 },
          ],
          language: "de",
        },
      ],
      ""
    ),
    ɾ: renderPhonemeExample(
      [
        {
          mainText: "pero",
          tooltipTexts: [{ text: "r", index: 2 }],
          language: "es",
        },
      ],
      "ɾ"
    ),
    c: renderPhonemeExample(
      [
        {
          mainText: "tyók",
          tooltipTexts: [
            { text: "t", index: 0 },
            { text: "y", index: 1 },
          ],
          language: "hun",
        },
      ],
      ""
    ),

    ʎ: renderPhonemeExample(
      [
        {
          mainText: "aglio",
          tooltipTexts: [{ text: "l", index: 2 }],
          language: "it",
        },
      ],
      ""
    ),

    ɥ: renderPhonemeExample(
      [
        {
          mainText: "nuire",
          tooltipTexts: [{ text: "u", index: 1 }],
          language: "fr",
        },
      ],
      ""
    ),
    ˈ: (
      <div>
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.primaryStress"
          )}
        </span>
      </div>
    ),
    ˌ: (
      <div>
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.secondaryStress"
          )}
        </span>
      </div>
    ),
    ".": (
      <div>
        <span id="ph-main">
          {t("tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.syllable")}
        </span>
      </div>
    ),
    ː: (
      <div>
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.lengthening"
          )}
        </span>
      </div>
    ),
    "̃": (
      <div>
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.nasalization"
          )}
        </span>
      </div>
    ),
    "`": (
      <div>
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.rhotacization"
          )}
        </span>
        <br />
        <span id="ph-main">
          {t(
            "tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.retroflexion"
          )}
        </span>
      </div>
    ),
    "\\": (
      <div>
        <span id="ph-main">
          {t("tts.toolbarMenu.pronunciationMenu.diacriticsTooltips.soundMod")}
        </span>
      </div>
    ),
  };
};
